import { create } from 'zustand'
import { deviceApi } from '../lib/deviceApi'

// State for the Data Logger (Live) device-settings page.
// Holds the currently-viewed device, its Sample/Log/Transmit config, and the
// async status of device actions (name update / reset / sync clock).
export const useDeviceStore = create((set, get) => ({
  device: null,
  config: null,
  isLoading: false,
  error: null,
  // Per-action transient status: { action: 'idle'|'pending'|'success'|'error', message }
  action: { name: 'idle', reset: 'idle', syncClock: 'idle', formatMemory: 'idle', message: null },

  /** Load device header + config together. */
  loadDevice: async (projectId, deviceId) => {
    set({ isLoading: true, error: null })
    try {
      const [detail, config] = await Promise.all([
        deviceApi.fetchDevice(projectId, deviceId),
        deviceApi.fetchConfig(projectId, deviceId),
      ])
      if (!detail.success) throw new Error(detail.error || 'Failed to load device')
      set({
        device: detail.device,
        config: config.success ? config : null,
        isLoading: false,
        error: config.success ? null : (config.error || null),
      })
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to load device'
      set({ isLoading: false, error: message })
      return { success: false, error: message }
    }
  },

  _setAction: (key, status, message = null) =>
    set((s) => ({ action: { ...s.action, [key]: status, message } })),

  /** Update the device name; refreshes local state on success. */
  updateName: async (projectId, deviceId, name) => {
    get()._setAction('name', 'pending')
    try {
      const res = await deviceApi.updateName(projectId, deviceId, name)
      if (!res.success) throw new Error(res.error || 'Update failed')
      set((s) => ({ device: s.device ? { ...s.device, name: res.name } : s.device }))
      get()._setAction('name', 'success', res.message)
      return { success: true, queuedToDevice: res.queued_to_device }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Update failed'
      get()._setAction('name', 'error', message)
      return { success: false, error: message }
    }
  },

  /** Queue a device-level command (reset / syncClock / formatMemory). */
  runAction: async (key, projectId, deviceId) => {
    const fnMap = {
      reset: deviceApi.resetDevice,
      syncClock: deviceApi.syncClock,
      formatMemory: deviceApi.formatMemory,
    }
    const fn = fnMap[key]
    if (!fn) return { success: false, error: `Unknown action: ${key}` }
    get()._setAction(key, 'pending')
    try {
      const res = await fn(projectId, deviceId)
      if (!res.success) throw new Error(res.error || 'Command failed')
      get()._setAction(key, 'success', res.message)
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Command failed'
      get()._setAction(key, 'error', message)
      return { success: false, error: message }
    }
  },

  clearActionStatus: (key) => get()._setAction(key, 'idle', null),
  clearError: () => set({ error: null }),
  reset: () => set({ device: null, config: null, isLoading: false, error: null,
    action: { name: 'idle', reset: 'idle', syncClock: 'idle', formatMemory: 'idle', message: null } }),
}))
